import axios from "axios";
import { baseContainer } from "../index";
import { toastActions } from "../redux/reducers/toastReducer";
import {
  setNetworkError,
  setPageError,
} from "../redux/reducers/sessionReducer";
import Logger from "../logger";
import i18n from "i18next";
import ErrorCodes from "../constants/errorCodes";
import { Auth0TokenError, ApiAuthTokenError } from "../errors";

export const formatUrlParams = (url, params) => {
  const newParams = {};

  for (let key in params) {
    if (url.indexOf(`{${key}}`) >= 0) {
      url = url.replace(`{${key}}`, params[key]);
    } else {
      newParams[key] = params[key];
    }
  }
  return { url, params: newParams };
};

const request = async ({
  method,
  url,
  data,
  params,
  contentType = "application/json",
  responseType = "json",
  nonceHeader = false,
}) => {
  try {
    const { token, authToken } =
      await baseContainer.authenticationService.getAuthToken();


    const r = formatUrlParams(url, params);

    Logger.of(`App.services.base.${method}`).info({ url: r.url, data });

    const stateSessionConfig = baseContainer.getStoreState().session.config || { authTokenHeader: "access_token", nonceTokenHeader: "nonce_token" };
    let nonceToken;
    if (nonceHeader) {
      let nonceResponse = await axios.request({
        method: "get",
        baseURL: baseContainer.getConfig().apiEndpoints.base,
        url: baseContainer.getConfig().apiEndpoints.nonceToken,
        headers: {
          Authorization: `Bearer ${token}`,
          [stateSessionConfig.authTokenHeader]: authToken,
          "Content-Type": contentType,
          "Accept-Language": i18n.language || "en",
        },
      });
      nonceToken = nonceResponse.data.token;
    }
    return axios.request({
      method: method,
      baseURL: baseContainer.getConfig().apiEndpoints.base,
      url: r.url,
      params: r.params,
      data: data,
      responseType: responseType,
      headers: {
        Authorization: `Bearer ${token}`,
        [stateSessionConfig.authTokenHeader]: authToken,
        "Content-Type": contentType,
        ...(nonceToken && {
          [stateSessionConfig.nonceTokenHeader]: nonceToken,
        }),
        "Accept-Language": i18n.language || "en",
      },
    });
  } catch (err) {
    // Attempt user logout if Auth0 token cannot be retrieved
    // or API auth token request fails
    if (err instanceof Auth0TokenError || err instanceof ApiAuthTokenError) {
      baseContainer.authenticationService.postLogout(false);
    } else {
      throw err;
    }
  }
};

axios.interceptors.response.use(
  (response) => {
    // Indicating no network error.
    baseContainer.store.dispatch(setNetworkError(false));
    return response;
  },
  (error) => {
    // Indicating no network error.
    baseContainer.store.dispatch(setNetworkError(false));
    if (error.code === "ERR_NETWORK") {
      baseContainer.store.dispatch(setNetworkError(true));
      return;
    }
    if (error.response) {
      //ignore ms graph error if user doesnt have a photo
      if (
        error.config.baseURL ===
        baseContainer.getConfig().graphConfig.graphEndpoint
      ) {
        return;
      }

      // Handle 5xx erros as page errors
      // with the exception of the grid API requests
      // Errors in grid requests are handled by the grid component
      if (
        (error.response.status >= 500 &&
          !error.request.responseURL.endsWith("/grid")) ||
        error.response.status === 404
      ) {
        baseContainer.store.dispatch(setPageError(true));
        // Handle 400x errors
      } else if (error.response.status >= 400 && error.response.status < 500) {
        let errorMsg = getErrorMessage(error.response.data);
        baseContainer.store.dispatch(toastActions.showErrorMessage(errorMsg));
      }

      if (
        // Session expiration error code indicates that the provided API auth
        // token is already invalid. There is no need for back-end logout request.
        error.response.data.ErrorCode === ErrorCodes.SESSION_EXPIRED ||
        // HTTP Status code is 401 unauthorized but error code does not indicate
        // unauthorized Core user access. We prevent redirect to login if user
        // is unauthorized, and display the unauthorized page.
        (error.response.status === 401 &&
          ((error.request.responseURL.includes(
            baseContainer.getConfig().apiEndpoints.identityCurrent
          ) &&
            error.response.data.ErrorCode !==
            ErrorCodes.UNAUTHORIZED_USER_ACCESS) ||
            // HTTP Status code is 401 unauthorized but error code does not
            // indicate unauthorized Baseline user access
            (error.request.responseURL.includes(
              baseContainer.getConfig().apiEndpoints.identityTenantToken
            ) &&
              error.response.data.ErrorCode !==
              ErrorCodes.UNAUTHORIZED_TENANT_USER_ACCESS)))
      ) {
        // In case an authorization token cannot be created then we are assuming
        // either some internal or network error or the Auth0 token is invalid
        baseContainer.authenticationService.postLogout(false);
      }
    } else {
      baseContainer.store.dispatch(
        toastActions.showErrorMessage(getErrorMessage(ErrorCodes.GENERIC))
      );
    }
    return Promise.reject(error.response);
  }
);

const deleteRequest = async ({ url, data = {}, params = {} }) => {
  return request({ method: "delete", url, data, params, nonceHeader: true });
};

const patch = async ({ url, data = {}, params = {} }) => {
  return request({ method: "patch", url, data, params, nonceHeader: true });
};

const put = async ({ url, data = {}, params = {} }) => {
  return request({ method: "put", url, data, params, nonceHeader: true });
};

const get = async ({ url, params = {} }) => {
  return request({ method: "get", url, params });
};

const post = async ({ url, data = {}, params = {}, nonceHeader = false }) => {
  return request({ method: "post", url, data, params, nonceHeader });
};

const getAnonymous = async ({ url, params = {} }) => {
  return axios.request({
    method: "get",
    baseURL: baseContainer.getConfig().apiEndpoints.base,
    url: url,
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": i18n.language || "en",
    },
  });
};

const postFile = async ({ url, data = {} }) => {
  return request({
    method: "post",
    url,
    data,
    contentType: "multipart/form-data",
    nonceHeader: true,
  });
};

const exportFile = async ({ url, params = {}, data, fileName, method }) => {
  const r = formatUrlParams(url, params);

  return request({
    method: method ?? "post",
    url: r.url,
    data,
    responseType: "blob",
  }).then((res) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([res.data]));
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
  });
};

class ParsedErrorMessage {
  constructor(errorData) {
    const isValidErrorData = errorData && typeof errorData === "object";

    this.errorCode = isValidErrorData ? errorData.ErrorCode ?? null : null;
    this.message = isValidErrorData ? errorData.Message ?? null : null;
    this.error = isValidErrorData ? errorData.Error ?? null : null;
    this.parameters = isValidErrorData && typeof errorData.Parameters === "object"
      ? errorData.Parameters
      : {};
  }

  sanitizeValue(value) {
    if (typeof value !== "string") {
      return String(value ?? "");
    }
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  getSanitizedParameters() {
    const sanitized = {};
    for (const [key, value] of Object.entries(this.parameters)) {
      sanitized[key] = this.sanitizeValue(value);
    }
    return sanitized;
  }

  interpolateMessage(template, params) {
    return template.replace(/\{\{([^}]+)\}\}/g, (_, key) => {
      const trimmedKey = key.trim();
      return Object.prototype.hasOwnProperty.call(params, trimmedKey)
        ? params[trimmedKey]
        : `{{${trimmedKey}}}`;
    });
  }

  getTranslatedMessage() {
    const sanitizedParams = this.getSanitizedParameters();
    const translationKey = `errorCodes.${this.errorCode}`;

    if (this.errorCode !== null && i18n.exists(translationKey)) {
      return i18n.t(translationKey, sanitizedParams);
    }

    if (this.message && typeof this.message === "string") {
      return this.interpolateMessage(this.message, sanitizedParams);
    }

    return i18n.t(`errorCodes.${ErrorCodes.GENERIC}`);
  }
}

function getErrorMessage(errorData) {
  const parsedError = new ParsedErrorMessage(errorData);
  return parsedError.getTranslatedMessage();
}

const base = {
  get,
  post,
  put,
  patch,
  deleteRequest,
  exportFile,
  postFile,
  getAnonymous,
};

export default base;
